import {
  cache,
  HYDRA_SHELL_INVALIDATION_SOURCE,
  type InvalidateCacheEntry,
  REALTIME_INVALIDATION_SOURCE,
} from '@customerio/hydra-nav-state';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { sentryCaptureException } from '@/utils/sentry';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

/**
 * Subscribes to cross-app cache invalidation events via hydra-nav-state's
 * `cache` namespace and invalidates all active React Query queries.
 *
 * When the copilot or another micro-frontend signals that data has changed
 * (e.g. after a tool execution), this hook debounces and then calls
 * `queryClient.invalidateQueries()` which marks all queries as stale
 * and refetches any that are currently active. Entries marked with the realtime
 * `source` are skipped on purpose: they are for the embedded editor, and a full refetch here on
 * every server signal would hit every viewer. Entries marked with the Hydra shell `source` are
 * skipped too: Hydra raised them after refreshing its own queries.
 *
 * Entry types in `targetedQueryKeys` refetch only that query, with no debounce. Read once, at mount. With
 * `targetedOnly`, every other entry is left alone: for a surface whose client another instance of this hook
 * already refetches in full, such as a page inside hydra's authenticated layout.
 *
 * Must be rendered inside a `QueryClientProvider`.
 */
export function useCacheInvalidation(
  targetedQueryKeys: ReadonlyMap<string, QueryKey> = new Map(),
  { targetedOnly = false }: { targetedOnly?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInvalidating = useRef(false);
  const pendingInvalidation = useRef(false);

  // queryClient is stable across renders so this is effectively a mount-only effect
  useMountEffect(() => {
    async function invalidateAll() {
      if (isInvalidating.current) {
        pendingInvalidation.current = true;
        return;
      }

      isInvalidating.current = true;

      try {
        await queryClient.invalidateQueries();
      } catch (error) {
        sentryCaptureException(error, {
          extra: {
            context: 'cache-invalidation',
            message: 'Failed to invalidate React Query cache',
          },
        });
      } finally {
        isInvalidating.current = false;

        if (pendingInvalidation.current) {
          pendingInvalidation.current = false;

          if (debounceTimer.current != null) {
            clearTimeout(debounceTimer.current);
          }

          debounceTimer.current = setTimeout(invalidateAll, 500);
        }
      }
    }

    function handleInvalidation(entry: InvalidateCacheEntry) {
      // Realtime entries are for the embedded app; refetching everything on each would hit every viewer.
      if (entry.source === REALTIME_INVALIDATION_SOURCE) return;
      // Hydra raised these for the embedded app after refreshing its own queries.
      if (entry.source === HYDRA_SHELL_INVALIDATION_SOURCE) return;

      const targetedQueryKey = targetedQueryKeys.get(entry.type);
      if (targetedQueryKey != null) {
        void queryClient.invalidateQueries({ queryKey: targetedQueryKey });
        return;
      }

      if (targetedOnly) return;

      if (debounceTimer.current != null) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(invalidateAll, 500);
    }

    const unsubscribe = cache.onInvalidate(handleInvalidation);

    return () => {
      unsubscribe();

      if (debounceTimer.current != null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }

      pendingInvalidation.current = false;
    };
  });
}
